import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAccount, useConnect, useDisconnect, useWalletClient } from 'wagmi';
import { useWallet as useSolanaWallet, useConnection } from '@solana/wallet-adapter-react';

// Create context for backward compatibility
const WalletContext = createContext(undefined);

/**
 * Dual wallet context supporting both EVM (wagmi) and Solana wallets
 * Maintains backward compatibility with existing EVM wallet code
 */
export function WalletProvider({ children }) {
  // EVM wallet hooks (wagmi)
  const { address, isConnected, connector } = useAccount();
  const { connect, connectors, isPending: isConnecting, error: connectError } = useConnect();
  const { disconnect } = useDisconnect();
  const { data: walletClient } = useWalletClient();

  // Solana wallet hooks
  const solanaWallet = useSolanaWallet();
  const { connection } = useConnection();

  // Track which wallet type is active ('evm' | 'solana')
  const [activeWalletType, setActiveWalletType] = useState(null);

  // Compatibility function for old connectWallet API
  const connectWallet = async () => {
    // Try to find the first available connector (usually injected/MetaMask)
    const connector = connectors[0];
    if (connector) {
      connect({ connector });
      setActiveWalletType('evm');
    }
  };

  // Connect Solana wallet
  const connectSolanaWallet = async () => {
    try {
      if (!solanaWallet.wallet) {
        // Trigger wallet selection modal
        await solanaWallet.select();
      }
      await solanaWallet.connect();
      setActiveWalletType('solana');
      console.log('✅ Solana wallet connected:', solanaWallet.publicKey?.toBase58());
    } catch (error) {
      console.error('❌ Failed to connect Solana wallet:', error);
      throw error;
    }
  };

  // Disconnect Solana wallet
  const disconnectSolanaWallet = async () => {
    try {
      await solanaWallet.disconnect();
      setActiveWalletType(null);
      console.log('✅ Solana wallet disconnected');
    } catch (error) {
      console.error('❌ Failed to disconnect Solana wallet:', error);
    }
  };

  const value = {
    // EVM wallet state
    isConnected,
    address,
    walletClient,
    error: connectError?.message || null,
    isConnecting,
    connectWallet,
    disconnectWallet: disconnect,
    connector,
    connectors,
    connect, // Expose wagmi's connect for advanced usage

    // Solana wallet state
    solanaWallet,
    solanaConnected: solanaWallet.connected,
    solanaAddress: solanaWallet.publicKey?.toBase58(),
    solanaWalletName: solanaWallet.wallet?.adapter?.name,
    connectSolanaWallet,
    disconnectSolanaWallet,

    // Wallet type management
    activeWalletType,
    setActiveWalletType,
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
}

export function useWallet() {
  const context = useContext(WalletContext);
  if (context === undefined) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
}
